"use client";

import React from "react";
import Image from "next/image";
import { Card, CardContent } from "@/components/hongkong-setup-v2/ui/card";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
const leftColumnContent = {
  title: "What Is a Hong Kong Payment Service Provider?",
  paragraphs: [
    "A Hong Kong Payment Service Provider (PSP) is a regulated financial platform that enables businesses to process payments without many of the restrictions found in traditional processors such as Stripe, PayPal, or 2Checkout. While typical payment platforms often freeze funds, limit usage, or unexpectedly shut down accounts, Hong Kong PSPs operate under a strong regulatory system built to support high-volume international transactions.",
    "These PSPs are designed for companies that need unlimited processing capabilities, multi-currency functionality, and protection from the account of disruptions commonly experienced with mainstream processors. Hong Kong's regulatory environment offers the stability, reliability, and freedom businesses need to scale without interruptions.",
  ],
  featuresTitle: "Key PSP Features:",
  features: [
    "No limits on daily or monthly processing volumes",
    "No unexpected account freezes or random reviews",
    "Supports multiple currencies (USD, EUR, GBP, HKD, and more)",
    "Handles international and cross-border payments",
    "Strong regulatory oversight and protection",
    "24/7 technical support and system monitoring",
  ],
};

const rightColumnContent = {
  title: "Why Choose a Hong Kong PSP Over Traditional Processors?",
  paragraphs: [
    'Mainstream processors like Stripe, PayPal, and 2Checkout have become increasingly restrictive, often placing sudden holds, freezing accounts, or banning high-volume businesses without clear justification. These companies focus on serving major corporations such as Amazon and Uber, while smaller or growing businesses are labeled "high risk" and subjected to constant scrutiny and limitations.',
    "Hong Kong PSPs solve these issues by offering enterprise-level payment infrastructure without arbitrary limitations. Hong Kong's regulatory system prioritizes business growth, making these PSPs a dependable and scalable option for entrepreneurs who need stable, high-capacity payment processing.",
  ],
  problemsTitle: "Problems With Traditional Processors:",
  problems: [
    "Unexpected account freezes",
    'Funds withheld for 120–180+ days during "reviews"',
    "Volume limits that lead to automatic suspensions",
    "Dispute processes that typically favor buyers",
    "Sudden policy changes that disrupt business operations",
  ],
  advantagesTitle: "Advantages of Hong Kong PSPs:",
  advantages: [
    "Stable, regulation-driven operations",
    "No caps on processing volumes",
    "Faster settlement times (1–3 business days)",
    "Fair and professional dispute handling",
    "Clear pricing with no hidden fees",
  ],
};

const cards = [
  {
    title: "Setup Process",
    description:
      "A complete Hong Kong PSP setup covers regulatory approval, technical integration, compliance documentation, and continuous support. The full implementation typically requires 2–4 weeks.",
    listTitle: "Included Services:",
    items: [
      "Compliance and regulatory assessment",
      "Technical system configuration",
      "Integration of the payment gateway",
      "Testing and quality assurance",
      "Go-live deployment with active monitoring",
      "Ongoing technical assistance",
    ],
  },
  {
    title: "Investment Analysis",
    description:
      "At a one-time cost of $4,000, a Hong Kong PSP setup delivers strong long-term value, especially when compared to the financial losses caused by the restrictions of traditional payment processors",
    listTitle: "Cost Comparison:",
    items: [
      "Frozen Stripe funds: Losses ranging from $10K to $100K+",
      "PayPal holds: Up to six months of disrupted revenue",
      "Rebuilding accounts after bans: $5K–$15K in expenses",
      "Hong Kong PSP setup: One-time $4,000 fee",
      "Unlimited processing capabilities",
      "No unexpected limits or account terminations",
    ],
  },
  {
    title: "Ideal Businesses",
    description:
      "A Hong Kong PSP is an excellent fit for fast-growing companies that require stable, unrestricted payment processing without the risk of sudden shutdowns or arbitrary reviews.",
    listTitle: "Perfect For:",
    items: [
      "E-commerce brands generating $50K+ monthly",
      "SaaS and subscription-based companies",
      "Digital marketing firms",
      "International trade businesses",
      "Fintech and crypto-related operations",
      "High-volume service providers",
    ],
  },
];

const technicalSpecs = {
  left: {
    title: "Processing Capabilities",
    items: [
      "Processing Volume: No daily or monthly transaction limits",
      "Transaction Speed: Instant, real-time authorization",
      "Settlement Time: Funds settled within 1–3 business days",
      "System Uptime: 99.9% service availability guaranteed",
      "Fraud Prevention: Enhanced machine-learning fraud monitoring",
      "API Support: RESTful API integration with available SDKs",
    ],
  },
  right: {
    title: "Regulatory Compliance",
    items: [
      "Licensed by the Hong Kong Monetary Authority",
      "Fully PCI DSS Level 1 compliant",
      "Meets GDPR and CCPA data protection standards",
      "Includes anti-money laundering (AML) safeguards",
      "Incorporates Know Your Customer (KYC) verification procedures",
      "Compliant with international regulatory standards.",
    ],
  },
};

export const MainContentSection = () => {

  return (
    <section className="flex flex-col w-full items-center py-6 md:py-8 lg:py-[70px]">
      <div className="flex flex-col items-start w-full max-w-[1440px] px-4 md:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-8 md:gap-12 lg:gap-[69px] pb-0 px-4 md:px-8 lg:px-16 xl:px-[149.26px] w-full">
          <header className="flex flex-col items-center gap-3 md:gap-4 lg:gap-[5px] w-full">
            {/* <h2 className=" text-[30px] leading-[34px] w-full max-w-[990px] mt-[-1.00px]  bg-gradient-to-r from-[rgba(21,93,252,1)] to-[rgba(255,255,255,1)] bg-clip-text  font-inter text-transparent text-3xl md:text-4xl lg:text-5xl xl:text-[56px] text-center md:leading-snug lg:leading-[67.5px]  tracking-[0] px-4">
              <span className="font-semibold">Complete </span>
              <span className="font-jomolhari text-normal font-normal">
                Hong Kong Payment{" "}
              </span>
              <span className="text-white font-semibold">Service Provider Guide 2025</span>
            </h2> */}
            <h2 className="max-w-[990px] text-center ">
              <span className="leading-[120%] bg-gradient-to-r from-[rgba(21,93,252,1)] to-[rgba(255,255,255,1)] bg-clip-text text-transparent text-[36px] lg:text-[70px]">
                Complete Hong Kong Payment Service
              </span>{" "}
              <span className="leading-[120%] text-white text-[36px] lg:text-[70px]">
                Provider Guide 2026
              </span>
            </h2>

            <p className="w-full">
              <span className="block w-full max-w-[835px] mx-auto font-inter font-[300] text-zinc-400 text-sm md:text-base lg:text-[16.9px] text-center tracking-[0] leading-relaxed md:leading-[26.3px] px-4">
                Professional insight into Hong Kong PSPs, payment regulations,
                regulatory compliance, and unlimited payment processing
                solutions
              </span>
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-[30.04px] w-full">
            <article className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-inter font-normal text-[#ffffff] text-base md:text-lg lg:text-[18.8px] tracking-[0] leading-relaxed lg:leading-[26.3px]">
                {leftColumnContent.title}
              </h2>
              <div className="flex flex-col gap-2 md:gap-3">
                {leftColumnContent.paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className="font-inter font-normal text-zinc-400 text-xs md:text-sm lg:text-[15.1px] tracking-[0] leading-relaxed lg:leading-[18.8px]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="flex flex-col gap-2 md:gap-3 mt-2 md:mt-4">
                <h3 className="font-inter font-normal text-[#ffffff] text-sm md:text-[15px] leading-relaxed md:leading-[22.5px] tracking-[0]">
                  {leftColumnContent.featuresTitle}
                </h3>
                <ul className="flex flex-col gap-1 md:gap-[5.63px]">
                  {leftColumnContent.features.map((feature, index) => (
                    <li
                      key={index}
                      className="font-inter font-normal text-zinc-400 text-xs md:text-sm lg:text-[15.1px] tracking-[0] leading-relaxed lg:leading-[18.8px]"
                    >
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </article>

            <article className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-inter font-normal text-[#ffffff] text-base md:text-lg lg:text-[18.8px] tracking-[0] leading-relaxed lg:leading-[26.3px]">
                {rightColumnContent.title}
              </h2>
              <div className="flex flex-col gap-4 md:gap-[22.26px]">
                {rightColumnContent.paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className="font-inter font-normal text-zinc-400 text-xs md:text-sm lg:text-[15.1px] tracking-[0] leading-relaxed lg:leading-[18.8px]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="flex flex-col gap-2 md:gap-3 mt-2">
                <h3 className="font-inter font-normal text-[#ffffff] text-sm md:text-[15px] tracking-[0] leading-relaxed md:leading-[22.5px]">
                  {rightColumnContent.problemsTitle}
                </h3>
                <ul className="flex flex-col gap-1 md:gap-[5.63px]">
                  {rightColumnContent.problems.map((problem, index) => (
                    <li
                      key={index}
                      className="font-inter font-normal text-zinc-400 text-xs md:text-sm lg:text-[15.1px] tracking-[0] leading-relaxed lg:leading-[18.8px]"
                    >
                      • {problem}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-2 md:gap-3 mt-2 md:mt-4">
                <h3 className="font-inter font-normal text-[#ffffff] text-sm md:text-[15px] tracking-[0] leading-relaxed md:leading-[22.5px]">
                  {rightColumnContent.advantagesTitle}
                </h3>
                <ul className="flex flex-col gap-1 md:gap-[5.63px]">
                  {rightColumnContent.advantages.map((advantage, index) => (
                    <li
                      key={index}
                      className="font-inter font-normal text-zinc-400 text-xs md:text-sm lg:text-[15.1px] tracking-[0] leading-relaxed lg:leading-[18.8px]"
                    >
                      • {advantage}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[23px] w-full">
            {cards.map((card, index) => (
              <Card
                key={index}
                className="bg-[#0a0f1e] rounded-2xl overflow-hidden border-0"
              >
                <CardContent className="p-[23px] relative">
                  <div className="top-[-69px] w-[238px] rounded-[119px] absolute left-[-17px] h-[238px] blur-[82px] bg-[linear-gradient(270deg,rgba(51,92,255,0.3)_0%,rgba(111,166,255,0.3)_100%)]" />

                  <div className="relative z-10 flex flex-col gap-4">
                    <h3 className="font-inter font-normal text-[#ffffff] text-[18.8px] tracking-[0] leading-[30.3px]">
                      {card.title}
                    </h3>

                    <p className="font-inter font-normal text-zinc-400 text-[13px] tracking-[0] leading-[20.8px]">
                      {card.description}
                    </p>

                    <div className="flex flex-col gap-3 mt-4">
                      <h4 className="font-inter font-normal text-[#ffffff] text-[13.1px] tracking-[0] leading-[18.8px]">
                        {card.listTitle}
                      </h4>

                      <ul className="flex flex-col gap-[5.63px]">
                        {card.items.map((item, itemIndex) => (
                          <li
                            key={itemIndex}
                            className="font-inter font-normal text-zinc-400 text-[13px] tracking-[0] leading-[18.8px]"
                          >
                            • {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-[#0a0f1e] rounded-2xl overflow-hidden border-0 w-full">
            <CardContent className="p-[30.98px] relative">
              <div className="top-[-120px] w-[1100px] rounded-[550px/119px] absolute left-[-17px] h-[238px] blur-[82px] bg-[linear-gradient(270deg,rgba(51,92,255,0.3)_0%,rgba(111,166,255,0.3)_100%)]" />
              <div className="relative z-10 flex flex-col gap-[30.04px]">
                <h2 className="font-inter font-normal text-[#ffffff] text-[28.2px] text-center tracking-[0] leading-[33.8px]">
                  Hong Kong PSP Technical Specifications
                </h2>
                <div className="relative z-10 flex flex-col gap-[30.04px]">
                  {/* Responsive Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[45.06px]">
                    <div className="flex flex-col gap-[15.02px]">
                      <h3 className="font-inter font-normal text-[#ffffff] text-[16.9px] tracking-[0] leading-[26.3px]">
                        {technicalSpecs.left.title}
                      </h3>
                      <ul className="flex flex-col gap-[7.51px]">
                        {technicalSpecs.left.items.map((item, index) => (
                          <li
                            key={index}
                            className="font-inter font-normal text-zinc-400 text-[15.1px] tracking-[0] leading-[18.8px]"
                          >
                            • {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col gap-[15.02px]">
                      <h3 className="font-inter font-normal text-[#ffffff] text-[16.9px] tracking-[0] leading-[26.3px]">
                        {technicalSpecs.right.title}
                      </h3>
                      <ul className="flex flex-col gap-[7.51px]">
                        {technicalSpecs.right.items.map((item, index) => (
                          <li
                            key={index}
                            className="font-inter font-normal text-zinc-400 text-[15.1px] tracking-[0] leading-[18.8px]"
                          >
                            • {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <section
        className="relative container w-full overflow-hidden bg-cover bg-center bg-no-repeat mt-3 mb-2 lg:mt-12 lg:mb-[70px] rounded-[17px]"
        style={{ backgroundImage: "url(/hongkong-setup-v2/div-lazy-background-image.png)" }}
      >
        <div className="absolute top-[-9.66%] left-[-5.32%] w-[110.64%] h-[31.46%] opacity-50 flex items-start pt-[221.34px]">
          <Image
            className="absolute w-full h-full top-[-4682.08%] left-[-2.65%]"
            alt="Div lazy background"
            src="/hongkong-setup-v2/div-lazy-background-image-mask.png"
            width={800}
            height={600}
          />
          <Image
            className="relative flex-1 h-px mb-[-1.00px]"
            alt="Div"
            src="/hongkong-setup-v2/div.svg"
            width={800}
            height={600}
          />
        </div>

        <Image
          className="absolute w-full h-full top-0 left-0 rounded-lg "
          alt="Image"
          src="/hongkong-setup-v2/bluebg.png"
          width={800}
          height={600}
        />

        <div className="relative flex flex-col items-center mx-auto px-0 lg:px-4  py-[70px] lg:py-[143px]">
          <h2 className="font-normal max-w-[1236px] text-[36px] lg:text-[70px] leading-[34px] text-center sm:leading-[48px] md:leading-[70px] pb-[16px] lg:leading-[70.128px] tracking-[0] [font-family:'SharpGroteskPeTrialSmBold-15',sans-serif] bg-[linear-gradient(180deg,rgba(255,255,255,1)_22%,rgba(255,255,255,0.7)_100%)] [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent] [text-fill-color:transparent]">
            Don&#39;t Let Payment Processors
            <br className="sm:block hidden"/>
            Destroy Your Business
          </h2>

          <p className="max-w-[620px] text-center text-[18px] lg:text-[26px]  font-inter">
            <span className="text-[#9b96b0] font-[300]">
              One setup with us prevents all the nightmares.{" "}
            </span>
            <br />
            <span className=" font-normal leading-[40px] text-[#ffffff]">
              Join 1000+ successful eCommerce entrepreneurs who chose
              protection.
            </span>
          </p>

          <div className="flex flex-col items-center gap-3 mt-1 lg:mt-8">
            <div className="flex w-auto flex-col sm:flex-row items-center justify-center mx-auto lg:w-full gap-3">
              <button
                type="button"
                onClick={() => openWhatsAppWithUtm(WA_NUMBERS, "hk-setup")}
                className="cursor-pointer
                h-9 px-4 py-2
    relative
    w-full sm:w-auto
    inline-flex items-center justify-center gap-2
     sm:py-6
     px-3 py-1.5
    rounded-xl
    bg-[#335cff]
    text-white
    font-bold
    overflow-hidden

    transition-all duration-300 ease-out
    hover:scale-[1.03]
    hover:-translate-y-1
    hover:shadow-[0_12px_30px_rgba(51,92,255,0.45)]
    hover:bg-gradient-to-r hover:from-[#476cff] hover:to-[#004eff]
    active:scale-[0.98]
  "
              >
                <div className="absolute w-[326px] h-[80px] right-[-28.5px] bottom-[-68px] rounded-[326px] bg-[#D9D9D9] blur-[27px]"></div>
                {/* Shine Effect */}
                <span className="absolute inset-0 overflow-hidden">
                  <span
                    className="
        absolute
        -left-full top-0
        h-full w-full
        bg-gradient-to-r
        from-transparent via-white/30 to-transparent
        opacity-0
        transition-all duration-700
        hover:opacity-100 hover:left-full
      "
                  />
                </span>

                <span className="uppercase relative text-[16px] font-figtree font-normal sm:text-base tracking-[-0.32px] leading-6 sm:leading-7">
                  CLAIM YOUR HK COMPANY NOW
                </span>
              </button>
            </div>

            <p className="font-normal text-sm leading-5 tracking-[0] text-zinc-400 text-center font-inter">
              ⚡ 100% Money-Back Guarantee
            </p>
          </div>
        </div>
      </section>
    </section>
  );
};

{
  /*Button Commented 
 <Button
                onClick={() => setShowCalendly(true)}
                className="
    relative
    w-auto
    inline-flex items-center justify-center gap-2
    px-3 py-1.5 sm:px-7 py-2.5 sm:py-6
    rounded-xl
    bg-[#212121]
    text-white
    font-bold
    overflow-hidden

    shadow-[inset_0px_0px_22px_#f2f2f280,inset_0px_0px_0px_1px_#999999,inset_-2px_-2px_1px_-2px_#b3b3b3,inset_2px_2px_1px_-2px_#b3b3b3]

    transition-all duration-300 ease-out
    hover:scale-[1.03]
    hover:-translate-y-1
    hover:bg-gradient-to-r hover:from-[#2a2a2a] hover:to-[#121212]
    hover:shadow-[0_12px_30px_rgba(0,0,0,0.65)]
    active:scale-[0.98]
  "
              >
               
                <span className="absolute inset-0 overflow-hidden">
                  <span
                    className="
        absolute
        -left-full top-0
        h-full w-full
        bg-gradient-to-r
        from-transparent via-white/20 to-transparent
        opacity-0
        transition-all duration-700
        hover:opacity-100 hover:left-full
      "
                  />
                </span>

                <span className="relative text-[9px] sm:text-base tracking-[-0.32px] leading-6 sm:leading-7">
                  BOOK A FREE CONSULTATION!
                </span>
              </Button>
*/
}
